import React, { useContext, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClone, faDownload, faEllipsisV, faEnvelope, faFolder, faLock, faLockOpen, faPen, faSitemap } from '@fortawesome/free-solid-svg-icons'
import UserContext from 'context/user-context'
import ConfirmDelete from 'shared/confirm-delete'
import {
  useUpdateReport,
  useDuplicateReport,
  useDestroyReport,
  useCreateReportPdfExport,
  useReportDocumentHtml
} from 'api/hooks/use-report-builder'
import MoveReportToFolderModal from './move-report-to-folder-modal'
import SplitReportModal from './split-report-modal'
import ScheduleEmailButton from './schedule-email-button'
import { isReportOwner, useToggleReportLock } from './report-lock-badge'

// Self-contained per-report actions kebab, reused on the list card and the builder header.
// Owns its own mutations + modals; callers only pass the report and optional post-action callbacks.
function ReportActionsMenu({ report, onDeleted, onDuplicated, align = 'end', toggleLabel, children }) {
  const user = useContext(UserContext)
  const queryClient = useQueryClient()
  const [showRename, setShowRename] = useState(false)
  const [showMove, setShowMove] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [showSplit, setShowSplit] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [name, setName] = useState(report.name || '')

  const isOwner = isReportOwner(report, user)
  // A child takes its edit access from the template's share list (editor_via_parent).
  const isEditor = (report.shared_users?.find((su) => su.user_id === user.id)?.is_editor ?? false) ||
    !!report.editor_via_parent
  // A child report renders its parent's layout, so nothing about it is editable — including its
  // name and the folder it sits in, both of which follow the parent.
  const isChild = !!report.is_child
  // A lock binds the owner too: they unlock first, then rename, move or delete.
  const isLocked = !!report.locked
  const canEdit = (isOwner || isEditor) && !isChild && !isLocked
  // Sharing, scheduling and export are the point of a child report — only *editing* is blocked.
  const canDistribute = isOwner || isEditor
  const childCount = report.child_reports_count || 0

  const { mutate: updateReport } = useUpdateReport({ options: {} })
  const { mutate: duplicateReport, isLoading: isDuplicating } = useDuplicateReport({ options: {} })
  const { mutate: destroyReport } = useDestroyReport({ options: {} })
  const { toggle: toggleLock, isLoading: isTogglingLock } = useToggleReportLock(report)
  const { mutate: exportPdf, isLoading: isExporting } = useCreateReportPdfExport({
    options: {
      onSuccess: () => toast.success('Report PDF will be available in Exports shortly')
    }
  })

  // Document reports export via print-ready HTML opened in a print window (browser print-to-PDF).
  const { mutate: exportDocument, isLoading: isExportingDoc } = useReportDocumentHtml()

  // Open the window synchronously inside the click (browsers only allow window.open during the user
  // gesture — opening it in the async onSuccess would be blocked as a popup), then fill it once the
  // HTML resolves. Sever `opener` so the popup can't script back into the app.
  const handleExportDocument = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      toast.error('Enable pop-ups to export this document')
      return
    }
    printWindow.document.write('<!doctype html><title>Generating…</title><p>Generating document…</p>')
    exportDocument(report.id, {
      onSuccess: (data) => {
        printWindow.document.open()
        printWindow.document.write(data.html)
        printWindow.document.close()
        try { printWindow.opener = null } catch { /* same-origin; guard defensively */ }
        printWindow.focus()
        printWindow.print()
      },
      onError: () => {
        printWindow.close()
        toast.error('Could not generate the document')
      }
    })
  }

  const refresh = () => {
    queryClient.invalidateQueries(['reports-builder'])
    queryClient.invalidateQueries([`reports/${report.id}`])
  }

  const handleRename = (e) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || trimmed === report.name) { setShowRename(false); return }
    updateReport({ id: report.id, name: trimmed }, {
      onSuccess: () => { refresh(); setShowRename(false); toast.success('Report renamed') },
      onError: () => toast.error('Could not rename the report')
    })
  }

  const handleDuplicate = () => {
    duplicateReport(report.id, {
      onSuccess: (newReport) => {
        queryClient.invalidateQueries(['reports-builder'])
        toast.success(`Duplicated as "${newReport?.name || 'Copy'}"`)
        onDuplicated?.(newReport)
      },
      onError: () => toast.error('Could not duplicate the report')
    })
  }

  const handleDelete = () => {
    destroyReport(report.id, {
      onSuccess: () => { queryClient.invalidateQueries(['reports-builder']); toast.success('Report deleted'); onDeleted?.() },
      onError: () => toast.error('Could not delete the report')
    })
  }

  return (
    <>
      {/* stopPropagation on pointerdown too, so the dnd-kit drag activator on the card wrapper doesn't swallow the toggle. */}
      <Dropdown onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        {toggleLabel ? (
          <Dropdown.Toggle variant="light" className="border d-flex align-items-center gap-2" aria-label="Report actions">
            <FontAwesomeIcon icon={faEllipsisV} />
            {toggleLabel}
          </Dropdown.Toggle>
        ) : (
          <Dropdown.Toggle variant="link" bsPrefix="btn" className="text-muted p-1 border-0 rounded" style={{ lineHeight: 1 }} aria-label="Report actions">
            <FontAwesomeIcon icon={faEllipsisV} />
          </Dropdown.Toggle>
        )}
        <Dropdown.Menu align={align}>
          {children}
          {canEdit && (
            <Dropdown.Item onClick={() => setShowSplit(true)}>
              <FontAwesomeIcon icon={faSitemap} fixedWidth className="me-2 text-muted" />
              Split report…
            </Dropdown.Item>
          )}
          {canDistribute && (
            <Dropdown.Item onClick={() => setShowSchedule(true)}>
              <FontAwesomeIcon icon={faEnvelope} fixedWidth className="me-2 text-muted" />
              Schedule email
            </Dropdown.Item>
          )}
          {/* TODO: re-enable "Export document" once chart rendering in the exported HTML is sorted.
              Hidden from the menu only — the endpoint, DocumentHtmlRenderer and handleExportDocument
              above are all still wired up, so restoring this is deleting these comment markers. */}
          {/*
          <Dropdown.Item onClick={handleExportDocument} disabled={isExportingDoc}>
            <FontAwesomeIcon icon={faDownload} fixedWidth className="me-2 text-muted" />
            Export document
          </Dropdown.Item>
          */}
          <Dropdown.Item onClick={() => exportPdf(report.id)} disabled={isExporting}>
            <FontAwesomeIcon icon={faDownload} fixedWidth className="me-2 text-muted" />
            Export PDF
          </Dropdown.Item>
          <Dropdown.Divider />
          {canEdit && (
            <Dropdown.Item onClick={() => { setName(report.name || ''); setShowRename(true) }}>
              <FontAwesomeIcon icon={faPen} fixedWidth className="me-2 text-muted" />
              Rename
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={handleDuplicate} disabled={isDuplicating}>
            <FontAwesomeIcon icon={faClone} fixedWidth className="me-2 text-muted" />
            Duplicate
          </Dropdown.Item>
          {canEdit && (
            <Dropdown.Item onClick={() => setShowMove(true)}>
              <FontAwesomeIcon icon={faFolder} fixedWidth className="me-2 text-muted" />
              Move to folder…
            </Dropdown.Item>
          )}
          {isOwner && (
            <Dropdown.Item onClick={() => toggleLock(!isLocked)} disabled={isTogglingLock}>
              <FontAwesomeIcon icon={isLocked ? faLockOpen : faLock} fixedWidth className="me-2 text-muted" />
              {isLocked ? 'Unlock report' : 'Lock report'}
            </Dropdown.Item>
          )}
          {isOwner && !isLocked && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => setShowDelete(true)}>Delete</Dropdown.Item>
            </>
          )}
          {/* Editors see Delete so the option isn't a mystery, but only the owner can delete. A
              disabled item swallows pointer events, so the tooltip hangs off a wrapper around it. */}
          {!isOwner && isEditor && (
            <>
              <Dropdown.Divider />
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip id={`report-delete-disabled-${report.id}`}>You cannot delete a report that you do not own</Tooltip>}
              >
                <div data-testid="report-delete-disabled">
                  <Dropdown.Item disabled aria-disabled="true">Delete</Dropdown.Item>
                </div>
              </OverlayTrigger>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <Modal show={showRename} onHide={() => setShowRename(false)} centered onClick={(e) => e.stopPropagation()}>
        <Form onSubmit={handleRename}>
          <Modal.Header closeButton>
            <Modal.Title className="h5">Rename report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Control autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light-primary" onClick={() => setShowRename(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!name.trim()}>Save</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <MoveReportToFolderModal report={report} show={showMove} setShow={setShowMove} onMoved={refresh} />

      <SplitReportModal report={report} canEdit={canEdit} show={showSplit} onHide={() => setShowSplit(false)} />

      {/* Mounted only while open: it fetches the report's scheduled emails, once per card on the index. */}
      {showSchedule && <ScheduleEmailButton report={report} hideTrigger show setShow={setShowSchedule} />}

      <ConfirmDelete
        id={report.id}
        handleDelete={handleDelete}
        show={showDelete}
        setShow={setShowDelete}
        hideButton
        message={
          childCount > 0
            ? `Delete "${report.name}" and its ${childCount} child report${childCount === 1 ? '' : 's'}? This cannot be undone.`
            : `Delete "${report.name}"? This cannot be undone.`
        }
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
      />
    </>
  )
}

export default ReportActionsMenu
